import type {
  DataFetchParams,
  DataFetchResponse,
  DynamicFilterOptionsPage,
  FilterOptionsParams,
} from '@/components/DataTable';
import {
  getBrowserAgentSitesFilterOptions,
  getBrowserAgentSitesView,
} from '@kirin/api-client/api/shadow-ai';
import type {
  BrowserAgentSitesFilterOptionField,
  BrowserAgentSitesSortField,
  BrowserAgentSitesViewSite,
} from '@kirin/api-client/contracts/shadow-ai';
import { apiClient } from '@/lib/api-client';

/**
 * Shared-table adapter for the Browser Agent Sites view (KIR-4704): page,
 * sort, search and column filters all go to the server, which applies them
 * before the page is cut.
 */
export async function fetchBrowserAgentSites(
  organizationId: string,
  params: DataFetchParams,
  signal?: AbortSignal
): Promise<DataFetchResponse<BrowserAgentSitesViewSite>> {
  const response = await getBrowserAgentSitesView(
    apiClient,
    organizationId,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: params.sort?.field as BrowserAgentSitesSortField | undefined,
      sortOrder: params.sort?.order,
      search: params.search || undefined,
    },
    { signal }
  );
  return {
    data: response.sites,
    pagination: response.pagination,
    meta: {
      activeFilters: [],
      search: params.search
        ? { value: params.search, fields: ['siteHost'] }
        : undefined,
    },
  };
}

/** Distinct Agent / Via values for the column's multi-select filter. */
export async function fetchBrowserAgentSitesFilterOptions(
  organizationId: string,
  field: BrowserAgentSitesFilterOptionField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getBrowserAgentSitesFilterOptions(
    apiClient,
    organizationId,
    {
      field,
      search: params.search || undefined,
      limit: params.limit,
      offset: params.offset,
    }
  );
  return {
    options: response.options,
    total: response.pagination.total,
    hasMore: response.pagination.hasMore,
    nextOffset: response.pagination.nextOffset ?? null,
  };
}
